import { useState, useRef, useEffect } from "react";
import { CameraProps } from "@/types/camera";
import { CameraButton } from "./CameraButton";
import { useMediaRecorder } from "@/hooks/useMediaRecorder";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";
import { captureException } from "@/lib/safe-sentry";
import { StoryConfig, DEFAULT_STORY_CONFIG } from "@/types/story";
import { useFullscreen } from "@/hooks/useFullscreen";
import { LoadingSpinner } from "@/components/ui/loading-spinner";

const MAX_VIDEO_DURATION = 15; // segundos

export const WebCamera = ({ onCapture, onClose, maxVideoDuration = MAX_VIDEO_DURATION }: CameraProps) => {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [storyConfig, setStoryConfig] = useState<StoryConfig>(DEFAULT_STORY_CONFIG);
  const [capturedMedia, setCapturedMedia] = useState<{ dataUrl: string; type: 'photo' | 'video' } | null>(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const durationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const { toast } = useToast();

  const { isRecording, recordedBlob, startRecording, stopRecording } = useMediaRecorder(stream);
  
  // Ativar fullscreen quando câmera estiver aberta
  const { isFullscreen } = useFullscreen(!capturedMedia);

  useEffect(() => {
    document.body.classList.add('ybr-modal-open');
    startCamera();
    
    return () => {
      document.body.classList.remove('ybr-modal-open');
      stopCamera();
    };
  }, []);

  // Processar mídia capturada automaticamente
  useEffect(() => {
    if (capturedMedia) {
      handleConfirm();
    }
  }, [capturedMedia]);

  // Timer para duração do vídeo
  useEffect(() => {
    if (isRecording) {
      setVideoDuration(0);
      durationTimerRef.current = setInterval(() => {
        setVideoDuration(prev => {
          const newDuration = prev + 0.1;
          if (newDuration >= maxVideoDuration) {
            stopRecording();
            if (durationTimerRef.current) {
              clearInterval(durationTimerRef.current);
            }
          }
          return newDuration;
        });
      }, 100);
    } else {
      if (durationTimerRef.current) {
        clearInterval(durationTimerRef.current);
      }
    }

    return () => {
      if (durationTimerRef.current) {
        clearInterval(durationTimerRef.current);
      }
    };
  }, [isRecording, maxVideoDuration]);

  // Processar blob do vídeo quando gravação terminar
  useEffect(() => {
    if (recordedBlob && !isRecording) {
      const videoUrl = URL.createObjectURL(recordedBlob);
      setCapturedMedia({ dataUrl: videoUrl, type: 'video' });
    }
  }, [recordedBlob, isRecording]);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'user',
          width: { ideal: 1080 },
          height: { ideal: 1920 },
          aspectRatio: { ideal: 9/16 },
          frameRate: { ideal: 30 },
        },
        audio: true,
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (error: any) {
      logger.error('Error accessing camera', error, { 
        component: 'WebCamera',
        action: 'startCamera'
      });

      if (import.meta.env.PROD) {
        captureException(error);
      }

      toast({
        title: "Erro ao acessar câmera",
        description: "Verifique as permissões do navegador",
        variant: "destructive",
      });
      onClose();
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
  };

  const takePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(video, 0, 0);

    const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
    setCapturedMedia({ dataUrl, type: 'photo' });

    // Flash effect
    const flash = document.createElement('div');
    flash.style.cssText = 'position:fixed;inset:0;background:white;z-index:9999;animation:flash 0.3s';
    document.body.appendChild(flash);
    setTimeout(() => flash.remove(), 300);
  };

  const handleConfirm = async () => {
    if (!capturedMedia) return;

    try {
      // Remover ybr-modal-open ANTES de chamar onCapture
      document.body.classList.remove('ybr-modal-open');

      if (capturedMedia.type === 'photo') {
        const blob = await fetch(capturedMedia.dataUrl).then(res => res.blob());
        onCapture({
          type: 'photo',
          dataUrl: capturedMedia.dataUrl,
          blob,
          storyConfig,
        });
      } else {
        if (recordedBlob) {
          onCapture({
            type: 'video',
            dataUrl: capturedMedia.dataUrl,
            blob: recordedBlob,
            storyConfig,
            duration: videoDuration,
          });
        }
      }
    } catch (error) {
      console.error('Error processing media:', error);
      // Restaurar classe em caso de erro
      document.body.classList.add('ybr-modal-open');
    }
  };

  const handleRetake = () => {
    setCapturedMedia(null);
    setVideoDuration(0);
    if (capturedMedia?.type === 'video') {
      URL.revokeObjectURL(capturedMedia.dataUrl);
    }
  };

  const handleClose = () => {
    document.body.classList.remove('ybr-modal-open');
    onClose();
  };

  const recordingProgress = (videoDuration / maxVideoDuration) * 100;

  return (
    <div className="camera-fullscreen fixed inset-0 z-[9999999] bg-black overflow-hidden" style={{ isolation: 'isolate' }}>
      <style>{`
        @keyframes flash {
          0%, 100% { opacity: 0; }
          50% { opacity: 1; }
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .camera-fullscreen {
          position: fixed;
          inset: 0;
          background: black;
          -webkit-user-select: none;
          user-select: none;
          overflow: hidden;
          touch-action: none;
          z-index: 9999999 !important;
          isolation: isolate;
          width: 100vw;
          height: 100vh;
          height: 100dvh;
        }
        .camera-fullscreen video {
          position: fixed;
          inset: 0;
          width: 100vw;
          height: 100vh;
          height: 100dvh;
          object-fit: cover;
          transform: translateZ(0);
          -webkit-transform: translateZ(0);
        }
        @supports (-webkit-touch-callout: none) {
          .camera-fullscreen,
          .camera-fullscreen video {
            height: 100vh;
            height: -webkit-fill-available;
          }
        }
      `}</style>
      
      {/* Close button - Melhor visibilidade */}
      <Button
        variant="ghost"
        size="icon"
        onClick={handleClose}
        className={cn(
          "absolute z-30",
          "w-12 h-12",
          "bg-black/60 backdrop-blur-md",
          "border-2 border-white/40",
          "hover:bg-black/80 hover:border-white/60",
          "shadow-xl",
          "text-white"
        )}
        style={{
          top: 'calc(1rem + env(safe-area-inset-top))',
          right: '1rem'
        }}
      >
        <X className="w-6 h-6" />
      </Button>

      {/* Video stream or preview */}
      {!capturedMedia ? (
        <>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="absolute inset-0 w-full h-full object-cover"
            style={{ 
              objectFit: 'cover',
              width: '100%',
              height: '100%'
            }}
          />
          <canvas ref={canvasRef} className="hidden" />

          {/* Badge de modo câmera */}
          <div 
            className="absolute left-1/2 -translate-x-1/2 z-20 pointer-events-none"
            style={{
              top: 'calc(env(safe-area-inset-top) + 1rem)'
            }}
          >
            {isRecording ? (
              <div className="bg-red-500 text-white px-4 py-2 rounded-full flex items-center gap-2 shadow-xl">
                <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
                <span className="font-medium text-sm">Gravando</span>
              </div>
            ) : (
              <div className="bg-black/60 backdrop-blur-md text-white px-4 py-2 rounded-full border border-white/20 shadow-xl">
                <span className="font-medium text-sm">📸 Modo Foto</span>
              </div>
            )}
          </div>

          {/* Guias visuais */}
          <div className="absolute inset-0 pointer-events-none z-10">
            {/* Grid de composição (regra dos terços) */}
            <div className="absolute inset-0 grid grid-cols-3 grid-rows-3 gap-0 opacity-30">
              {[...Array(4)].map((_, i) => (
                <div key={`v-${i}`} className="border-l border-white/20" style={{ gridColumn: i + 1 }} />
              ))}
              {[...Array(4)].map((_, i) => (
                <div key={`h-${i}`} className="border-t border-white/20" style={{ gridRow: i + 1 }} />
              ))}
            </div>
            
            {/* Círculo guia para rosto */}
            <div 
              className="absolute left-1/2 -translate-x-1/2 w-64 h-64 rounded-full border-2 border-primary/40"
              style={{
                top: 'calc(25% + env(safe-area-inset-top))'
              }}
            />
          </div>

          {/* Camera button */}
          <CameraButton
            onPhoto={takePhoto}
            onVideoStart={startRecording}
            onVideoStop={stopRecording}
            isRecording={isRecording}
            recordingProgress={recordingProgress}
          />

          {/* Indicador de progresso da gravação */}
          {isRecording && (
            <div 
              className="absolute z-20 flex flex-col items-center gap-2"
              style={{
                bottom: 'calc(env(safe-area-inset-bottom) + 120px)',
                left: '50%',
                transform: 'translateX(-50%)'
              }}
            >
              {/* Barra de progresso */}
              <div className="w-64 h-2 bg-black/40 backdrop-blur-sm rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-red-500 to-red-600 transition-all duration-100"
                  style={{ width: `${recordingProgress}%` }}
                />
              </div>
              
              {/* Timer */}
              <div className="bg-black/60 backdrop-blur-md text-white px-3 py-1 rounded-full">
                <span className="font-mono text-sm font-bold">
                  {Math.floor(videoDuration)}s / {maxVideoDuration}s
                </span>
              </div>
            </div>
          )}
        </>
        ) : (
          // Loading durante processamento
          <div className="flex items-center justify-center h-full">
            <LoadingSpinner size="lg" />
          </div>
        )}
    </div>
  );
};
